import React from 'react';
import { Chip } from '@mui/material';
import {
  CheckCircle,
  Warning,
  Cancel,
  RadioButtonUnchecked,
} from '@mui/icons-material';

interface StatusBadgeProps {
  status: 'good' | 'warning' | 'error' | 'unknown';
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, children, size = 'md' }) => {
  // Soft tinted pill (light fill + dark same-family text) — lighter than a
  // solid chip and matches the card mockups.
  const getColors = (): { bg: string; fg: string } => {
    switch (status) {
      case 'good':
        return { bg: '#E1F5EE', fg: '#0f6e56' };
      case 'warning':
        return { bg: '#FAEEDA', fg: '#9a6a00' };
      case 'error':
        return { bg: '#FDECEA', fg: '#A32D2D' };
      default:
        return { bg: '#EDEDED', fg: '#5f5f5f' };
    }
  };

  // Shape paired with the colour (tick / triangle / cross / open-circle) so the
  // state is distinguishable without relying on colour alone (WCAG 1.4.1). The
  // Chip tints the icon to match the label, so no colour-on-colour clash.
  const getIcon = (): React.ReactElement => {
    switch (status) {
      case 'good':
        return <CheckCircle />;
      case 'warning':
        return <Warning />;
      case 'error':
        return <Cancel />;
      default:
        return <RadioButtonUnchecked />;
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return { height: 22, px: 0.5, fontSize: '0.7rem', iconSize: 13 };
      case 'lg':
        return { height: 30, px: 1, fontSize: '0.85rem', iconSize: 18 };
      default:
        return { height: 26, px: 0.75, fontSize: '0.8rem', iconSize: 16 };
    }
  };

  const { bg, fg } = getColors();
  const { iconSize, ...sizeStyles } = getSizeStyles();

  return (
    <Chip
      label={children}
      icon={getIcon()}
      variant="filled"
      sx={{
        ...sizeStyles,
        fontWeight: 500,
        borderRadius: '999px',
        backgroundColor: bg,
        color: fg,
        '& .MuiChip-icon': { color: fg, fontSize: iconSize, ml: '6px', mr: '-3px' },
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
};
